import posthog from 'posthog-js';

import type { PipelineTypeFilter } from 'client/types.gen';

type IntroductionStatusType = 'global' | 'pipeline' | 'talent_fyi';

type CaptureFn = ((
  // A new pipeline has been created, i.e. a user confirmed the new pipeline
  // creation by selecting Open on the new pipeline form/Create a new
  // search/Create a new talent FYI pipeline
  event_name: 'open_pipeline',
  properties: {
    pipeline_slug: string;
    // TODO: Jess explain?
    // TODO: when flags['project/collections'] is removed,
    // we no longer need 'pipeline' in this type
    pipeline_type: PipelineTypeFilter | 'pipeline';
    team_slug: string;
  },
) => void) &
  ((
    // A new introduction was requested
    event_name: 'request_introduction',
    properties: {
      candidate_id: string;
      pipeline_slug?: string;
      pipeline_type: IntroductionStatusType;
      requester_id: string;
      team_slug: string;
    },
  ) => void) &
  ((
    // An introduction has been facilitated
    event_name: 'facilitate_introduction',
    properties: {
      candidate_id: string;
      pipeline_slug?: string;
      pipeline_type: IntroductionStatusType;
      requester_id: string;
      team_slug: string;
    },
  ) => void) &
  ((
    // A candidate is added to a Pipeline/Trending
    event_name: 'add_candidate',
    properties: {
      candidate_id: string;
      pipeline_slug?: string;
      pipeline_type: 'pipeline' | 'talent_fyi';
      team_slug: string;
    },
  ) => void) &
  ((
    // User added a candidate to an existing pipeline
    event_name: 'user_add_candidate',
    properties: {
      candidate_id: string;
      pipeline_slug: string;
      pipeline_type: 'pipeline';
      team_slug: string;
      user_id: string;
    },
  ) => void) &
  ((
    event_name: 'share_candidates',
    properties: {
      candidates_id: string[];
      sharer_id: string;
      sharing_method: 'copy' | 'email';
    },
  ) => void) &
  ((
    event_name: 'add_candidate_comment',
    properties: {
      candidate_id: string;
      user_id: string;
    },
  ) => void) &
  ((
    event_name: 'delete_candidate_comment',
    properties: {
      candidate_id: string;
      user_id: string;
    },
  ) => void) &
  ((
    event_name: 'edit_candidate_comment',
    properties: {
      candidate_id: string;
      user_id: string;
    },
  ) => void) &
  ((
    // User added a candidate to favorites
    event_name: 'save_candidate',
    properties: {
      candidate_id: string;
      user_id: string;
    },
  ) => void) &
  ((
    // User removed a candidate from favorites
    event_name: 'unsave_candidate',
    properties: {
      candidate_id: string;
      user_id: string;
    },
  ) => void) &
  ((
    // We manually refreshed the tab because the user left their tab open for
    // too long
    event_name: 'refresh_due_to_stale_client',
    properties: {
      timeout_seconds: number;
      user_id: string;
    },
  ) => void) &
  ((
    // A lazy chunk failed to load (usually a stale deploy) and we reloaded
    // the page once to recover
    event_name: 'chunk_load_recovery_reload',
    properties: {
      reason: string;
    },
  ) => void) &
  ((
    // A recovery reload booted and stayed healthy for 10 seconds
    event_name: 'chunk_load_recovery_succeeded',
    properties: Record<string, never>,
  ) => void) &
  ((
    // A chunk failed again right after a recovery reload; we showed the
    // error page instead of reloading again
    event_name: 'chunk_load_recovery_failed',
    properties: {
      reason: string;
    },
  ) => void) &
  ((
    // A form was submitted, by any input method (mouse, keyboard, or
    // programmatic). `outcome` distinguishes a successful submit from one
    // blocked by client-side (zod) validation; `invalid_fields` lists the
    // failing field keys when blocked.
    event_name: 'form_submitted',
    properties: {
      form: string;
      outcome: 'success' | 'validation_error';
      invalid_fields?: string[];
    },
  ) => void);

const posthogToken = import.meta.env.VITE_APP_POSTHOG_TOKEN;

export const captureOkr: CaptureFn = (event_name, properties) => {
  if (!posthogToken) {
    return;
  }

  posthog.capture(event_name, properties);
};
